/**
 * EventsPage — real-time event feed with image preview and supervisor validation.
 * Design inspired by security monitoring dashboards:
 * - Left: large image preview of selected event
 * - Right: scrollable event list with True/False/Clear validation
 * - Auto-refreshes every 15s
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle,
  FormControl, IconButton, InputAdornment, InputLabel, MenuItem, Paper, Select,
  Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import CheckCircleIcon    from '@mui/icons-material/CheckCircle';
import CancelIcon         from '@mui/icons-material/Cancel';
import RemoveCircleIcon   from '@mui/icons-material/RemoveCircle';
import RefreshIcon        from '@mui/icons-material/Refresh';
import VideocamIcon       from '@mui/icons-material/Videocam';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CameraAltIcon      from '@mui/icons-material/CameraAlt';
import PublicIcon         from '@mui/icons-material/Public';
import CloseIcon          from '@mui/icons-material/Close';
import ZoomInIcon         from '@mui/icons-material/ZoomIn';
import ZoomOutIcon        from '@mui/icons-material/ZoomOut';
import FitScreenIcon      from '@mui/icons-material/FitScreen';
import FullscreenIcon     from '@mui/icons-material/Fullscreen';
import DownloadIcon       from '@mui/icons-material/Download';
import DeleteIcon         from '@mui/icons-material/Delete';
import ContentCopyIcon    from '@mui/icons-material/ContentCopy';
import OpenInNewIcon      from '@mui/icons-material/OpenInNew';
import SearchIcon         from '@mui/icons-material/Search';
import { FixedSizeList }  from 'react-window';
import {
  getEvents, getDevices, getLocations, getDepartments, getShifts, getSupervisors,
  getImageUrl, getActivityScreenshotUrl, getActivityBrowserHistoryEntries,
  deleteActivityScreenshot, validateEvent, buzzEventAlarm, isAdmin, isSupervisor,
} from '../api/client';
import useDetectionSettings, { enabled } from '../hooks/useDetectionSettings';
import useTimezone from '../hooks/useTimezone';
import { ensureTz, formatDateTime, formatTime } from '../utils/format';

const PAGE_SIZE = 100;

const TYPE_COLORS = {
  drowsiness:      '#f44336',
  yawning:         '#ff9800',
  distracted:      '#9c27b0',
  both:            '#e91e63',
  screenshot:      '#00acc1',
  browser_history: '#3f51b5',
};
const TYPE_LABELS = {
  drowsiness:      'Drowsiness',
  yawning:         'Yawning',
  distracted:      'Distracted',
  both:            'Both',
  screenshot:      'Screenshot',
  browser_history: 'Browser History',
};
const TYPE_EMOJI = {
  drowsiness:      '😴',
  yawning:         '🥱',
  distracted:      '👀',
  both:            '⚠️',
  screenshot:      '📷',
  browser_history: '🌐',
};

// Resolve a thumbnail/preview URL for any unified-timeline event type —
// detection events use image_ref (existing token-in-query pattern);
// screenshot events have no image_ref (see db/models.py::Event docstring)
// and instead go through the existing Activity screenshot-image endpoint.
function getEventPreviewUrl(event) {
  if (!event) return null;
  if (event.image_ref) return getImageUrl(event.image_ref);
  if (event.detection_type === 'screenshot' && event.screenshot_id) {
    return getActivityScreenshotUrl(event.screenshot_id);
  }
  return null;
}

// Measures a container's pixel size via ResizeObserver — used to size the
// virtualized browser-history list, which needs a concrete pixel height
// (no react-virtualized-auto-sizer dependency in this project).
function useElementSize() {
  const ref = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setSize({ width, height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, size];
}

const DARK_BG    = '#1a1a2e';
const CARD_BG    = '#16213e';
const PANEL_BG   = '#0f3460';
const TEXT_MAIN  = '#e0e0e0';
const TEXT_MUTED = '#9e9e9e';

// ── Validation badge ──────────────────────────────────────────────────────────
function StatusBadge({ isValid }) {
  if (isValid === true)  return (
    <Chip size="small" label="TRUE" icon={<CheckCircleIcon sx={{ fontSize: 12 }} />}
      sx={{ bgcolor: '#1b5e20', color: '#a5d6a7', fontWeight: 700,
            fontSize: '0.65rem', height: 20, '& .MuiChip-icon': { color: '#a5d6a7' } }} />
  );
  if (isValid === false) return (
    <Chip size="small" label="FALSE"
      sx={{ bgcolor: '#b71c1c', color: '#ef9a9a', fontWeight: 700,
            fontSize: '0.65rem', height: 20, border: '1px solid #ef9a9a' }} />
  );
  return (
    <Chip size="small" label="PENDING"
      sx={{ bgcolor: 'transparent', color: TEXT_MUTED, fontWeight: 600,
            fontSize: '0.65rem', height: 20, border: `1px solid ${TEXT_MUTED}` }} />
  );
}

const ALARM_STATUS_LABEL = {
  alarm_enabled:       'Alarm Enabled',
  alarm_disabled:      'Alarm Disabled',
  detection_disabled:  'Detection Disabled',
  not_licensed:        'Not Licensed',
};

function AlarmStatusChip({ alarm }) {
  if (!alarm) return null;
  const label = ALARM_STATUS_LABEL[alarm.status] || alarm.status;
  const color = alarm.status === 'alarm_enabled' ? '#1b5e20' : 'transparent';
  const textColor = alarm.status === 'alarm_enabled' ? '#a5d6a7' : TEXT_MUTED;
  return (
    <Chip size="small" label={label}
      sx={{ bgcolor: color, color: textColor, fontWeight: 600,
            fontSize: '0.65rem', height: 20, border: `1px solid ${textColor}` }} />
  );
}

// ── Right-side event card ─────────────────────────────────────────────────────
function EventCard({ event, selected, onClick, onValidated, tz }) {
  const [validating, setValidating] = useState(false);

  // Use event.is_valid from parent (updated via onValidated) — NOT local state
  // Local state would be stale after parent updates the events array
  const localValid = event.is_valid;

  const handleValidate = async (e, val) => {
    e.stopPropagation();
    setValidating(true);
    try {
      await validateEvent(event.id, val);
      onValidated?.(event.id, val);  // parent updates the events array
    } catch (err) {
      console.error(err);
    } finally { setValidating(false); }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onValidated?.(event.id, null);
  };

  const typeColor = TYPE_COLORS[event.detection_type] || '#757575';
  const imgUrl    = getEventPreviewUrl(event);
  const time      = formatTime(event.started_at, tz);
  const date      = event.started_at
    ? new Date(ensureTz(event.started_at)).toLocaleDateString(undefined, { timeZone: tz })
    : '';

  const cardBg = localValid === false ? 'rgba(183,28,28,0.25)'
               : localValid === true  ? 'rgba(27,94,32,0.2)'
               : selected ? 'rgba(15,52,96,0.9)'
               : 'rgba(22,33,62,0.9)';

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex', gap: 1.5, p: 1.5, mb: 1, borderRadius: 1.5,
        bgcolor: cardBg,
        border: selected ? '1px solid #1976d2' : '1px solid rgba(255,255,255,0.07)',
        cursor: 'pointer', transition: 'all 0.15s',
        '&:hover': { border: '1px solid rgba(255,255,255,0.2)', bgcolor: 'rgba(30,50,80,0.9)' },
      }}
    >
      {/* Thumbnail */}
      <Box sx={{ flexShrink: 0 }}>
        {imgUrl ? (
          <Box component="img" src={imgUrl} alt="event"
            sx={{ width: 56, height: 42, objectFit: 'cover', borderRadius: 1,
                  border: `2px solid ${typeColor}` }}
            onError={e => { e.target.style.display = 'none'; }}
          />
        ) : (
          <Box sx={{ width: 56, height: 42, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.4)',
                     border: `2px solid ${typeColor}`, display: 'flex',
                     alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
            {TYPE_EMOJI[event.detection_type] || '?'}
          </Box>
        )}
      </Box>

      {/* Info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Chip label={TYPE_LABELS[event.detection_type] || event.detection_type}
            size="small"
            sx={{ bgcolor: typeColor, color: 'white', fontWeight: 700,
                  fontSize: '0.6rem', height: 18 }} />
          <StatusBadge isValid={localValid} />
        </Stack>
        <Typography variant="caption" sx={{ color: TEXT_MUTED, display: 'block', mt: 0.3 }}>
          {date}, {time}
          {event.duration_seconds ? ` · ${event.duration_seconds.toFixed(1)}s` : ''}
        </Typography>
        <Typography variant="caption" sx={{ color: TEXT_MUTED, display: 'block' }} noWrap>
          {event.operator_name ? `👤 ${event.operator_name}` : ''}
          {event.device_name   ? ` · 📹 ${event.device_name.slice(0, 12)}` : ''}
        </Typography>

        {/* Validation buttons */}
        {(isSupervisor() || isAdmin()) && (
          <Stack direction="row" spacing={0.5} mt={0.8}>
            <Button size="small" disabled={validating || localValid === true}
              onClick={e => handleValidate(e, true)}
              startIcon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
              sx={{
                py: 0.2, px: 1, fontSize: '0.65rem', minWidth: 0,
                bgcolor: localValid === true ? '#2e7d32' : 'transparent',
                color: '#66bb6a', border: '1px solid #2e7d32',
                '&:hover': { bgcolor: '#2e7d32', color: 'white' },
              }}>
              TRUE
            </Button>
            <Button size="small" disabled={validating || localValid === false}
              onClick={e => handleValidate(e, false)}
              startIcon={<CancelIcon sx={{ fontSize: '14px !important' }} />}
              sx={{
                py: 0.2, px: 1, fontSize: '0.65rem', minWidth: 0,
                bgcolor: localValid === false ? '#c62828' : 'transparent',
                color: '#ef5350', border: '1px solid #c62828',
                '&:hover': { bgcolor: '#c62828', color: 'white' },
              }}>
              FALSE
            </Button>
            {localValid !== null && localValid !== undefined && (
              <Button size="small" disabled={validating}
                onClick={handleClear}
                startIcon={<RemoveCircleIcon sx={{ fontSize: '14px !important' }} />}
                sx={{
                  py: 0.2, px: 1, fontSize: '0.65rem', minWidth: 0,
                  color: TEXT_MUTED, border: `1px solid ${TEXT_MUTED}`,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                }}>
                CLEAR
              </Button>
            )}
            {validating && <CircularProgress size={14} sx={{ color: TEXT_MUTED, alignSelf: 'center' }} />}
          </Stack>
        )}
      </Box>
    </Box>
  );
}

// ── Full Image Viewer (zoom / fit screen / download / fullscreen / delete) ────
function ImageViewerDialog({ event, open, onClose, onDeleted }) {
  const [zoom, setZoom] = useState(1);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const imgBoxRef = useRef(null);
  const url = getEventPreviewUrl(event);
  const canDelete = isAdmin() && event?.detection_type === 'screenshot' && event?.screenshot_id;

  useEffect(() => { if (open) { setZoom(1); setError(''); } }, [open, event?.id]);

  const download = () => {
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = `${event.detection_type}-${event.id}.jpg`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const goFullscreen = () => {
    const el = imgBoxRef.current;
    if (el?.requestFullscreen) el.requestFullscreen().catch(() => {});
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this screenshot permanently? This cannot be undone.')) return;
    setDeleting(true); setError('');
    try {
      await deleteActivityScreenshot(event.screenshot_id);
      onDeleted?.(event.id);
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'Delete failed');
    } finally { setDeleting(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { bgcolor: '#0a0a1a', color: TEXT_MAIN } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Typography variant="subtitle1" fontWeight={700}>
          {TYPE_LABELS[event?.detection_type] || 'Image'} Viewer
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Zoom Out"><span>
            <IconButton size="small" onClick={() => setZoom(z => Math.max(0.25, z - 0.25))} sx={{ color: TEXT_MAIN }}>
              <ZoomOutIcon />
            </IconButton>
          </span></Tooltip>
          <Tooltip title="Zoom In"><span>
            <IconButton size="small" onClick={() => setZoom(z => Math.min(4, z + 0.25))} sx={{ color: TEXT_MAIN }}>
              <ZoomInIcon />
            </IconButton>
          </span></Tooltip>
          <Tooltip title="Fit Screen"><span>
            <IconButton size="small" onClick={() => setZoom(1)} sx={{ color: TEXT_MAIN }}>
              <FitScreenIcon />
            </IconButton>
          </span></Tooltip>
          <Tooltip title="Fullscreen"><span>
            <IconButton size="small" onClick={goFullscreen} sx={{ color: TEXT_MAIN }}>
              <FullscreenIcon />
            </IconButton>
          </span></Tooltip>
          <Tooltip title="Download"><span>
            <IconButton size="small" onClick={download} disabled={!url} sx={{ color: TEXT_MAIN }}>
              <DownloadIcon />
            </IconButton>
          </span></Tooltip>
          {canDelete && (
            <Tooltip title="Delete"><span>
              <IconButton size="small" onClick={handleDelete} disabled={deleting} sx={{ color: '#ef5350' }}>
                {deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
              </IconButton>
            </span></Tooltip>
          )}
          <IconButton size="small" onClick={onClose} sx={{ color: TEXT_MAIN }}><CloseIcon /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ p: 0, bgcolor: '#000' }}>
        {error && <Alert severity="error" sx={{ m: 1 }}>{error}</Alert>}
        <Box ref={imgBoxRef} sx={{ height: '75vh', overflow: 'auto', display: 'flex',
                   alignItems: zoom <= 1 ? 'center' : 'flex-start',
                   justifyContent: zoom <= 1 ? 'center' : 'flex-start', bgcolor: '#000' }}>
          {url ? (
            <Box component="img" src={url} alt="full view"
              sx={{ transform: `scale(${zoom})`, transformOrigin: 'top left',
                    maxWidth: zoom <= 1 ? '100%' : 'none', maxHeight: zoom <= 1 ? '100%' : 'none' }} />
          ) : (
            <Typography sx={{ color: TEXT_MUTED }}>No image available</Typography>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

// Row height for both the plain and virtualized browser-history table body.
const HISTORY_ROW_HEIGHT = 34;
// Above this many (filtered) rows, switch to windowed rendering so the DOM
// never holds more than a screenful of row nodes at once.
const HISTORY_VIRTUALIZE_THRESHOLD = 500;

function HistoryColumnHeader() {
  return (
    <Box sx={{ display: 'flex', px: 1, py: 0.75, bgcolor: CARD_BG, flexShrink: 0,
               borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
      <Box sx={{ width: 76, flexShrink: 0, color: TEXT_MUTED, fontWeight: 700, fontSize: '0.7rem' }}>Time</Box>
      <Box sx={{ width: 90, flexShrink: 0, color: TEXT_MUTED, fontWeight: 700, fontSize: '0.7rem' }}>Browser</Box>
      <Box sx={{ width: 200, flexShrink: 0, color: TEXT_MUTED, fontWeight: 700, fontSize: '0.7rem' }}>Title</Box>
      <Box sx={{ flex: 1, minWidth: 0, color: TEXT_MUTED, fontWeight: 700, fontSize: '0.7rem' }}>URL</Box>
      <Box sx={{ width: 64, flexShrink: 0 }} />
    </Box>
  );
}

function HistoryRow({ entry, tz, copiedUrl, onCopy, style }) {
  return (
    <Box style={style} sx={{ display: 'flex', alignItems: 'center', px: 1,
                              borderBottom: '1px solid rgba(255,255,255,0.05)',
                              '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' } }}>
      <Box sx={{ width: 76, flexShrink: 0, color: TEXT_MAIN, fontSize: '0.75rem' }}>
        {entry.visited_at ? formatTime(entry.visited_at, tz) : '—'}
      </Box>
      <Box sx={{ width: 90, flexShrink: 0, color: TEXT_MAIN, fontSize: '0.75rem',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', pr: 0.5 }}>
        {entry.browser || '—'}
      </Box>
      <Box sx={{ width: 200, flexShrink: 0, color: TEXT_MAIN, fontSize: '0.75rem',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', pr: 1 }}>
        {entry.title || '(untitled)'}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, color: TEXT_MUTED, fontSize: '0.7rem',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {entry.url}
      </Box>
      <Stack direction="row" spacing={0.5} sx={{ width: 64, flexShrink: 0 }}>
        <Tooltip title={copiedUrl === entry.url ? 'Copied!' : 'Copy URL'}>
          <IconButton size="small" onClick={() => onCopy(entry.url)} sx={{ color: TEXT_MUTED }}>
            <ContentCopyIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Open in New Tab">
          <IconButton size="small" component="a" href={entry.url} target="_blank" rel="noreferrer"
            sx={{ color: TEXT_MUTED }}>
            <OpenInNewIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
}

// ── Browser History inline panel — opens immediately with the event, no
// extra click required. Fixed header (browser/count/captured-at + search)
// that never scrolls; only the row list below it scrolls. Rendered directly
// inside the Event Details (left) panel in place of the image preview.
function BrowserHistoryInline({ event, tz }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [search, setSearch]   = useState('');
  const [copiedUrl, setCopiedUrl] = useState('');
  const [bodyRef, bodySize] = useElementSize();

  useEffect(() => {
    setEntries([]); setSearch(''); setError('');
    if (!event?.browser_history_id) return;
    setLoading(true);
    getActivityBrowserHistoryEntries(event.browser_history_id)
      .then(setEntries)
      .catch(e => setError(e.response?.data?.detail || 'Failed to load browser history'))
      .finally(() => setLoading(false));
  }, [event?.browser_history_id]);

  const filtered = entries.filter(e => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (e.title || '').toLowerCase().includes(q) || (e.url || '').toLowerCase().includes(q);
  });

  const copyUrl = (url) => {
    navigator.clipboard?.writeText(url).then(() => {
      setCopiedUrl(url);
      setTimeout(() => setCopiedUrl(''), 1500);
    });
  };

  const browserLabel = event.browsers?.length ? event.browsers.join(', ') : (entries[0]?.browser || 'Browser');
  const urlCount = typeof event.entry_count === 'number' ? event.entry_count : entries.length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden' }}>
      {/* Fixed header — never scrolls */}
      <Box sx={{ p: 2, pb: 1.5, flexShrink: 0, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <PublicIcon sx={{ color: TYPE_COLORS.browser_history }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: TEXT_MAIN }}>
            {browserLabel}
          </Typography>
        </Stack>
        <Typography variant="body2" sx={{ color: TEXT_MUTED, mt: 0.5 }}>
          {urlCount} {urlCount === 1 ? 'URL' : 'URLs'} · Captured {formatDateTime(event.started_at, tz)}
        </Typography>
        <TextField
          size="small" fullWidth placeholder="Search title or URL…" value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ mt: 1.5, '& .MuiOutlinedInput-root': { color: TEXT_MAIN, bgcolor: 'rgba(255,255,255,0.05)' } }}
          InputProps={{ startAdornment: (
            <InputAdornment position="start"><SearchIcon sx={{ color: TEXT_MUTED, fontSize: 18 }} /></InputAdornment>
          ) }}
        />
      </Box>

      {error && <Alert severity="error" sx={{ m: 1, flexShrink: 0 }}>{error}</Alert>}

      {/* Scrollable body — only this area scrolls */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ textAlign: 'center', pt: 6 }}><CircularProgress size={28} /></Box>
        ) : filtered.length === 0 ? (
          <>
            <HistoryColumnHeader />
            <Typography sx={{ color: TEXT_MUTED, textAlign: 'center', pt: 4 }}>
              {entries.length === 0 ? 'No browser history entries' : 'No entries match your search'}
            </Typography>
          </>
        ) : (
          <>
            <HistoryColumnHeader />
            <Box ref={bodyRef} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
              {filtered.length > HISTORY_VIRTUALIZE_THRESHOLD ? (
                bodySize.height > 0 && (
                  <FixedSizeList
                    height={bodySize.height}
                    width="100%"
                    itemCount={filtered.length}
                    itemSize={HISTORY_ROW_HEIGHT}
                  >
                    {({ index, style }) => (
                      <HistoryRow entry={filtered[index]} tz={tz} copiedUrl={copiedUrl} onCopy={copyUrl} style={style} />
                    )}
                  </FixedSizeList>
                )
              ) : (
                <Box sx={{ height: '100%', overflow: 'auto',
                           '&::-webkit-scrollbar': { width: 6 },
                           '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 3 } }}>
                  {filtered.map((e, i) => (
                    <HistoryRow key={i} entry={e} tz={tz} copiedUrl={copiedUrl} onCopy={copyUrl}
                      style={{ height: HISTORY_ROW_HEIGHT }} />
                  ))}
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function EventsPage() {
  const detectionSettings = useDetectionSettings();
  const [events,    setEvents]    = useState([]);
  const [total,     setTotal]     = useState(0);
  const [page,      setPage]      = useState(1);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [selected,  setSelected]  = useState(null);
  const [buzzing,   setBuzzing]   = useState(false);
  const [buzzResult, setBuzzResult] = useState(null);
  const [devices,   setDevices]   = useState([]);
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [shifts,      setShifts]      = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [newCount,  setNewCount]  = useState(0);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const newestIdRef = useRef(null);

  const EMPTY_FILTERS = {
    device_id: '', detection_type: '', is_valid: '', location_id: '',
    from_date: '', to_date: '', department_id: '', shift_id: '', supervisor_id: '',
    event_source: '', capture_mode: '', browser: '', search: '',
  };
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [pendingFilters, setPendingFilters] = useState({ ...filters });

  const tz = useTimezone();

  useEffect(() => {
    getDevices().then(setDevices).catch(() => {});
    if (isAdmin()) {
      getLocations().then(setLocations).catch(() => {});
      getDepartments().then(setDepartments).catch(() => {});
      getShifts().then(setShifts).catch(() => {});
      getSupervisors().then(setSupervisors).catch(() => {});
    }
  }, []);

  const load = useCallback(async (pg = 1, merge = false) => {
    setLoading(true); setError('');
    try {
      const params = {
        page: pg, limit: PAGE_SIZE, tz,
        ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== '')),
      };
      const data = await getEvents(params);
      const incoming = data.events || [];
      setTotal(data.total || 0);

      if (merge && pg === 1 && newestIdRef.current) {
        const newIdx = incoming.findIndex(e => e.id === newestIdRef.current);
        const genuinelyNew = newIdx > 0 ? incoming.slice(0, newIdx) : [];
        if (genuinelyNew.length > 0) {
          setNewCount(c => c + genuinelyNew.length);
          setEvents(prev => {
            const ids = new Set(prev.map(e => e.id));
            return [...genuinelyNew.filter(e => !ids.has(e.id)), ...prev];
          });
          newestIdRef.current = incoming[0]?.id;
        }
      } else {
        setEvents(incoming);
        setPage(pg);
        if (incoming[0]) newestIdRef.current = incoming[0].id;
        setNewCount(0);
        // Auto-select first event
        setSelected(prev => prev || incoming[0] || null);
      }
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally { setLoading(false); }
  }, [filters, tz]);

  useEffect(() => { load(1); }, [load]);
  useEffect(() => {
    const t = setInterval(() => load(1, true), 15_000);
    return () => clearInterval(t);
  }, [load]);

  const applyFilters = () => { setFilters({ ...pendingFilters }); setPage(1); };
  const resetFilters = () => {
    setFilters({ ...EMPTY_FILTERS }); setPendingFilters({ ...EMPTY_FILTERS }); setPage(1);
  };

  const handleValidated = (id, val) => {
    setEvents(prev => prev.map(e => e.id === id ? { ...e, is_valid: val } : e));
    if (selected?.id === id) setSelected(s => ({ ...s, is_valid: val }));
  };

  const handleScreenshotDeleted = (eventId) => {
    setEvents(prev => prev.filter(e => e.id !== eventId));
    setTotal(t => Math.max(0, t - 1));
    if (selected?.id === eventId) setSelected(null);
  };

  const selectedImgUrl = getEventPreviewUrl(selected);
  const totalPages = Math.ceil(total / PAGE_SIZE);

  // Summary counts
  const counts = {};
  events.forEach(e => { counts[e.detection_type] = (counts[e.detection_type] || 0) + 1; });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 48px)',
               bgcolor: DARK_BG, color: TEXT_MAIN, overflow: 'hidden' }}>

      {/* ── Top bar ── */}
      <Box sx={{ px: 2, py: 1, bgcolor: CARD_BG, borderBottom: '1px solid rgba(255,255,255,0.08)',
                 display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" fontWeight={700}>
          Events
          {newCount > 0 && (
            <Chip label={`+${newCount} new`} size="small" color="warning"
              sx={{ ml: 1, height: 18, fontSize: '0.6rem', fontWeight: 700, cursor: 'pointer' }}
              onClick={() => setNewCount(0)} />
          )}
        </Typography>
        {total > 0 && (
          <Typography variant="caption" sx={{ color: TEXT_MUTED }}>
            {total.toLocaleString()} total
          </Typography>
        )}
        {Object.entries(counts).map(([t, c]) => (
          <Chip key={t} size="small" label={`${TYPE_EMOJI[t]} ${c}`}
            sx={{ height: 18, fontSize: '0.65rem',
                  bgcolor: TYPE_COLORS[t] || '#757575', color: 'white' }} />
        ))}
        <Box sx={{ flex: 1 }} />

        <TextField size="small" placeholder="Search operator, device, URL…"
          value={pendingFilters.search}
          onChange={e => setPendingFilters(f => ({ ...f, search: e.target.value }))}
          onKeyDown={e => { if (e.key === 'Enter') applyFilters(); }}
          sx={{ width: 200,
                '& .MuiInputBase-input': { fontSize: '0.75rem', color: TEXT_MAIN, py: 0.6 },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' } }}
          InputProps={{ startAdornment: (
            <InputAdornment position="start"><SearchIcon sx={{ color: TEXT_MUTED, fontSize: 16 }} /></InputAdornment>
          ) }} />

        {/* Filters inline — Event Source narrows which further filters are
            relevant (Capture Mode/Browser only apply to Activity events;
            Status only applies to Detection events). */}
        {[
          { label: 'Source', key: 'event_source', opts: [
            { v: '', l: 'All Sources' },
            { v: 'detection', l: 'Detection' },
            { v: 'activity',  l: 'Activity' },
          ]},
          { label: 'Type', key: 'detection_type', opts: [
            { v: '', l: 'All Types' },
            { v: 'drowsiness', l: '😴 Drowsiness' },
            { v: 'yawning',    l: '🥱 Yawning' },
            { v: 'distracted', l: '👀 Distracted' },
            { v: 'both',       l: '⚠️ Both' },
            { v: 'screenshot',      l: '📷 Screenshot' },
            { v: 'browser_history', l: '🌐 Browser History' },
          ].filter(o => !o.v || (o.v === 'both'
            ? enabled(detectionSettings, 'drowsiness') && enabled(detectionSettings, 'yawning')
            : enabled(detectionSettings, o.v)))
            .filter(o => !pendingFilters.event_source || o.v === ''
              || (pendingFilters.event_source === 'activity'
                ? ['screenshot', 'browser_history'].includes(o.v)
                : !['screenshot', 'browser_history'].includes(o.v)))},
          ...(pendingFilters.event_source !== 'activity' ? [{ label: 'Status', key: 'is_valid', opts: [
            { v: '', l: 'All Status' },
            { v: 'pending', l: '⏳ Pending' },
            { v: 'true',    l: '✅ True' },
            { v: 'false',   l: '❌ False' },
          ] }] : []),
          ...(pendingFilters.event_source === 'activity'
              || ['screenshot', 'browser_history'].includes(pendingFilters.detection_type) ? [
            { label: 'Capture Mode', key: 'capture_mode', opts: [
              { v: '', l: 'Any Mode' },
              { v: 'AUTO',   l: 'Auto' },
              { v: 'MANUAL', l: 'Manual' },
            ] },
          ] : []),
          ...(pendingFilters.event_source === 'activity'
              || pendingFilters.detection_type === 'browser_history' ? [
            { label: 'Browser', key: 'browser', opts: [
              { v: '', l: 'Any Browser' },
              { v: 'chrome', l: 'Chrome' },
              { v: 'edge',   l: 'Edge' },
              { v: 'brave',  l: 'Brave' },
            ] },
          ] : []),
        ].map(({ label, key, opts }) => (
          <FormControl key={key} size="small" sx={{ minWidth: 110 }}>
            <Select value={pendingFilters[key]}
              onChange={e => setPendingFilters(f => ({ ...f, [key]: e.target.value }))}
              displayEmpty
              sx={{ fontSize: '0.75rem', color: TEXT_MAIN, height: 30,
                    bgcolor: 'rgba(255,255,255,0.05)',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' },
                    '& .MuiSvgIcon-root': { color: TEXT_MUTED } }}>
              {opts.map(o => <MenuItem key={o.v} value={o.v} sx={{ fontSize: '0.8rem' }}>{o.l}</MenuItem>)}
            </Select>
          </FormControl>
        ))}

        <TextField type="date" size="small" label="From" value={pendingFilters.from_date}
          onChange={e => setPendingFilters(f => ({ ...f, from_date: e.target.value }))}
          InputLabelProps={{ shrink: true, sx: { color: TEXT_MUTED } }}
          sx={{ width: 135, '& .MuiInputBase-input': { fontSize: '0.75rem', color: TEXT_MAIN, py: 0.6 },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' } }} />
        <TextField type="date" size="small" label="To" value={pendingFilters.to_date}
          onChange={e => setPendingFilters(f => ({ ...f, to_date: e.target.value }))}
          InputLabelProps={{ shrink: true, sx: { color: TEXT_MUTED } }}
          sx={{ width: 135, '& .MuiInputBase-input': { fontSize: '0.75rem', color: TEXT_MAIN, py: 0.6 },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' } }} />

        {isAdmin() && [
          { key: 'location_id',   label: 'All Locations',   items: locations },
          { key: 'department_id', label: 'All Departments', items: departments },
          { key: 'shift_id',      label: 'All Shifts',      items: shifts },
          { key: 'supervisor_id', label: 'All Supervisors', items: supervisors.map(s => ({ id: s.id, name: s.name })) },
        ].filter(({ items }) => items.length > 0).map(({ key, label, items }) => (
          <FormControl key={key} size="small" sx={{ minWidth: 110 }}>
            <Select value={pendingFilters[key]}
              onChange={e => setPendingFilters(f => ({ ...f, [key]: e.target.value }))}
              displayEmpty
              sx={{ fontSize: '0.75rem', color: TEXT_MAIN, height: 30,
                    bgcolor: 'rgba(255,255,255,0.05)',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' } }}>
              <MenuItem value="" sx={{ fontSize: '0.8rem' }}>{label}</MenuItem>
              {items.map(l => <MenuItem key={l.id} value={l.id} sx={{ fontSize: '0.8rem' }}>{l.name}</MenuItem>)}
            </Select>
          </FormControl>
        ))}

        <Button size="small" variant="contained" onClick={applyFilters}
          sx={{ height: 30, fontSize: '0.7rem', bgcolor: '#1565c0' }}>
          Apply
        </Button>
        <Button size="small" onClick={resetFilters}
          sx={{ height: 30, fontSize: '0.7rem', color: TEXT_MUTED }}>
          Reset
        </Button>
        <Button size="small" startIcon={<RefreshIcon />} onClick={() => load(1)} disabled={loading}
          sx={{ height: 30, fontSize: '0.7rem', color: TEXT_MUTED }}>
          {loading ? <CircularProgress size={12} /> : 'Refresh'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mx: 2, mt: 1 }}>{error}</Alert>}

      {/* ── Main content: image preview left + event list right ── */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', gap: 0 }}>

        {/* Left: Image preview */}
        <Box sx={{ width: '55%', display: 'flex', flexDirection: 'column',
                   bgcolor: '#0a0a1a', borderRight: '1px solid rgba(255,255,255,0.08)' }}>

          {/* Image / Browser History inline view */}
          <Box sx={{ flex: 1, display: 'flex', alignItems: selected?.detection_type === 'browser_history' ? 'stretch' : 'center',
                     justifyContent: selected?.detection_type === 'browser_history' ? 'stretch' : 'center',
                     position: 'relative', overflow: 'hidden' }}>
            {selected?.detection_type === 'browser_history' ? (
              <BrowserHistoryInline event={selected} tz={tz} />
            ) : selectedImgUrl ? (
              <Box component="img" src={selectedImgUrl} alt="selected event"
                sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', cursor: 'zoom-in' }}
                onClick={() => setImageViewerOpen(true)}
                onError={e => { e.target.src = ''; }}
              />
            ) : (
              <Box sx={{ textAlign: 'center', color: TEXT_MUTED }}>
                <VideocamIcon sx={{ fontSize: 60, opacity: 0.3 }} />
                <Typography variant="body2" sx={{ opacity: 0.4, mt: 1 }}>
                  {loading ? 'Loading...' : 'Select an event to preview'}
                </Typography>
              </Box>
            )}

            {/* Event info overlay on image — the browser-history inline panel
                renders its own fixed header instead, so it's excluded here */}
            {selected && selected.detection_type !== 'browser_history' && (
              <Box sx={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                background: 'linear-gradient(transparent, rgba(0,0,0,0.85))',
                p: 2,
              }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={1} flexWrap="wrap">
                  <Chip label={TYPE_LABELS[selected.detection_type] || selected.detection_type}
                    icon={selected.detection_type === 'screenshot' ? <CameraAltIcon sx={{ fontSize: '14px !important', color: 'white !important' }} />
                        : undefined}
                    sx={{ bgcolor: TYPE_COLORS[selected.detection_type], color: 'white',
                          fontWeight: 700 }} />
                  {!['screenshot', 'browser_history'].includes(selected.detection_type) && (
                    <StatusBadge isValid={selected.is_valid} />
                  )}
                  <AlarmStatusChip alarm={selected.alarm} />
                </Stack>
                <Typography variant="body2" sx={{ color: 'white' }}>
                  {formatDateTime(selected.started_at, tz)}
                  {selected.duration_seconds ? ` · ${selected.duration_seconds.toFixed(1)}s` : ''}
                </Typography>
                {selected.device_name && (
                  <Typography variant="caption" sx={{ color: '#ccc', display: 'block' }}>
                    📷 {selected.device_name}
                  </Typography>
                )}
                {selected.operator_name && (
                  <Typography variant="caption" sx={{ color: '#ccc' }}>
                    👤 {selected.operator_name}
                    {selected.operator_employee_id ? ` (#${selected.operator_employee_id})` : ''}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* Validation action buttons — not applicable to Activity events
              (screenshot/browser_history have no is_valid/alarm semantics,
              see db/models.py::Event docstring) */}
          {selected && !['screenshot', 'browser_history'].includes(selected.detection_type)
            && (isSupervisor() || isAdmin()) && (
            <Box sx={{ p: 2, bgcolor: '#0d1117',
                       borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <Stack direction="row" spacing={1.5} justifyContent="center">
                <Button
                  variant={selected.is_valid === true ? 'contained' : 'outlined'}
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  onClick={async () => {
                    try {
                      await validateEvent(selected.id, true);
                      handleValidated(selected.id, true);
                    } catch (e) { console.error(e); }
                  }}
                  sx={{ minWidth: 130, py: 1.2, fontWeight: 700, fontSize: '0.9rem',
                        ...(selected.is_valid === true ? {} : {
                          borderColor: '#4caf50', color: '#4caf50',
                          '&:hover': { bgcolor: '#1b5e20' }
                        }) }}>
                  TRUE ALERT
                </Button>

                <Button
                  variant={selected.is_valid === false ? 'contained' : 'outlined'}
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={async () => {
                    try {
                      await validateEvent(selected.id, false);
                      handleValidated(selected.id, false);
                    } catch (e) { console.error(e); }
                  }}
                  sx={{ minWidth: 130, py: 1.2, fontWeight: 700, fontSize: '0.9rem',
                        ...(selected.is_valid === false ? {} : {
                          borderColor: '#f44336', color: '#f44336',
                          '&:hover': { bgcolor: '#b71c1c' }
                        }) }}>
                  FALSE ALERT
                </Button>

                {(selected.is_valid !== null && selected.is_valid !== undefined) && (
                  <Button variant="outlined"
                    startIcon={<RemoveCircleIcon />}
                    onClick={async () => {
                      handleValidated(selected.id, null);
                    }}
                    sx={{ minWidth: 100, py: 1.2, color: TEXT_MUTED,
                          borderColor: 'rgba(255,255,255,0.2)',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}>
                    CLEAR
                  </Button>
                )}

                {selected.alarm?.can_buzz && (
                  <Button variant="outlined" color="warning"
                    startIcon={buzzing ? <CircularProgress size={14} /> : <NotificationsActiveIcon />}
                    disabled={buzzing}
                    onClick={async () => {
                      setBuzzing(true);
                      setBuzzResult(null);
                      try {
                        const result = await buzzEventAlarm(selected.id);
                        setBuzzResult(result);
                      } catch (e) {
                        setBuzzResult({ success: false, reason: e.response?.data?.detail || 'Buzz failed' });
                      } finally {
                        setBuzzing(false);
                      }
                    }}
                    sx={{ minWidth: 130, py: 1.2, fontWeight: 700, fontSize: '0.9rem' }}>
                    Buzz Again
                  </Button>
                )}
              </Stack>
              {buzzResult && (
                <Alert severity={buzzResult.success ? 'success' : 'error'}
                       onClose={() => setBuzzResult(null)} sx={{ mt: 1.5 }}>
                  {buzzResult.success ? 'Alarm triggered successfully.' : (buzzResult.reason || 'Buzz failed')}
                </Alert>
              )}
            </Box>
          )}
          {selected && !selected.alarm?.can_buzz && selected.alarm && (isSupervisor() || isAdmin()) && (
            <Box sx={{ px: 2, pb: 1.5, bgcolor: '#0d1117' }}>
              <Typography variant="caption" color="text.secondary">
                {ALARM_STATUS_LABEL[selected.alarm.status] || selected.alarm.status}
                {' — Buzz Again is unavailable for this event.'}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Right: Event list */}
        <Box sx={{ width: '45%', display: 'flex', flexDirection: 'column',
                   bgcolor: DARK_BG, overflow: 'hidden' }}>

          {/* List header */}
          <Box sx={{ px: 2, py: 1, bgcolor: CARD_BG,
                     borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" sx={{ color: TEXT_MUTED, fontWeight: 600 }}>
                {loading ? 'LOADING...' : `${events.length} EVENTS SHOWN`}
              </Typography>
              <Stack direction="row" spacing={0.5}>
                <Chip label="ALL ALERTS" size="small"
                  sx={{ height: 20, fontSize: '0.6rem', bgcolor: '#1565c0', color: 'white',
                        cursor: 'pointer' }}
                  onClick={() => setPendingFilters(f => ({ ...f, is_valid: '' }))} />
              </Stack>
            </Stack>
          </Box>

          {/* Scrollable list */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5,
                     '&::-webkit-scrollbar': { width: 4 },
                     '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                     '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.15)',
                                                      borderRadius: 2 } }}>
            {loading && events.length === 0 ? (
              <Box sx={{ textAlign: 'center', pt: 6 }}>
                <CircularProgress size={32} sx={{ color: '#1976d2' }} />
              </Box>
            ) : events.length === 0 ? (
              <Typography sx={{ color: TEXT_MUTED, textAlign: 'center', pt: 6 }}>
                No events found
              </Typography>
            ) : events.map(event => (
              <EventCard
                key={event.id}
                event={event}
                tz={tz}
                selected={selected?.id === event.id}
                onClick={() => { setSelected(event); setBuzzResult(null); setImageViewerOpen(false); }}
                onValidated={handleValidated}
              />
            ))}

            {/* Load more */}
            {page < totalPages && (
              <Box sx={{ textAlign: 'center', pt: 1 }}>
                <Button size="small" disabled={loading}
                  onClick={() => load(page + 1)}
                  sx={{ color: '#1976d2', fontSize: '0.75rem' }}>
                  Load more ({total - events.length} remaining)
                </Button>
              </Box>
            )}
          </Box>

          {/* Auto-refresh footer */}
          <Box sx={{ px: 2, py: 0.5, bgcolor: CARD_BG,
                     borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <Typography variant="caption" sx={{ color: TEXT_MUTED }}>
              🔄 Auto-refreshes every 15s
            </Typography>
          </Box>
        </Box>
      </Box>

      <ImageViewerDialog
        event={selected}
        open={imageViewerOpen && Boolean(selectedImgUrl)}
        onClose={() => setImageViewerOpen(false)}
        onDeleted={handleScreenshotDeleted}
      />
    </Box>
  );
}
